<template lang="pug">
div
  v-card.round-xl.fill-height.pa-4(outlined)
    div.text-xs.font-medium.neutral700--text Vital signs
    v-card.round-xl.mt-2(outlined)
      v-simple-table.table-custom(dense)
        thead
          tr
            th.neutral500--text.px-2(v-if="showColumn('height')" style="text-transform: none !important; font-size: 10px !important;") Ht (cm)
            th.neutral500--text.px-2(v-if="showColumn('weight')" style="text-transform: none !important; font-size: 10px !important;") Wt (kg)
            th.neutral500--text.px-2(v-if="showColumn('temperature')" style="text-transform: none !important; font-size: 10px !important;") Temp (C)
            th.neutral500--text.px-2(v-if="showColumn('bloodPressure')" style="text-transform: none !important; font-size: 10px !important;") BP (mmHg)
            th.neutral500--text.px-2(v-if="showColumn('pulse')" style="text-transform: none !important; font-size: 10px !important;") HR (bpm)
            th.neutral500--text.px-2(v-if="showColumn('spo2')" style="text-transform: none !important; font-size: 10px !important;") SpO2
            th.neutral500--text.px-2(v-if="showColumn('bloodGlucose')" style="text-transform: none !important; font-size: 10px !important;") BG mmol/L
            th.neutral500--text.px-2(v-if="showColumn('respiratoryRate')" style="text-transform: none !important; font-size: 10px !important;") RR
        tbody
          template(v-if="isFetching")
            tr
              td.text-xs.neutral700--text.px-0.py-3.text-center(:colspan="visibleColumnsCount")
                v-progress-linear(indeterminate color="primary")
          template(v-else-if="vitalsigns.length")
            template(v-for="(vitalsign, idx) in vitalsigns")
              tr(:key="`vs-${idx}`")
                td.text-xs.neutral700--text.px-2(v-if="showColumn('height')") {{ parseFloat(vitalsign.height) || '-' }}
                td.text-xs.neutral700--text.px-2(v-if="showColumn('weight')") {{ parseFloat(vitalsign.weight) || '-' }}
                td.text-xs.neutral700--text.px-2(v-if="showColumn('temperature')") {{ vitalsign.temperature ? `${parseFloat(vitalsign.temperature)}°` : '-' }}
                td.text-xs.neutral700--text.px-2(v-if="showColumn('bloodPressure')") {{ vitalsign.bp_systolic ? `${vitalsign.bp_systolic}/ ${vitalsign.bp_diastolic}` : '-' }}
                td.text-xs.neutral700--text.px-2(v-if="showColumn('pulse')") {{ vitalsign.pulse ? `${vitalsign.pulse }` : '-' }}
                td.text-xs.neutral700--text.px-2(v-if="showColumn('spo2')") {{ vitalsign.spo2 ? `${vitalsign.spo2 }%` : '-' }}
                td.text-xs.neutral700--text.px-2(v-if="showColumn('bloodGlucose')") {{ vitalsign.blood_glucose || '-' }}
                td.text-xs.neutral700--text.px-2(v-if="showColumn('respiratoryRate')") {{ vitalsign.respiratory_rate || '-' }}
          template(v-else)
            tr
              td.text-xs.neutral700--text.px-2.text-center(:colspan="visibleColumnsCount") No vital signs recorded.
    div.d-flex.align-center.mt-3
      div
        y-checkbox(v-model="HideEmptyColumns" :disabled="vitalsigns.length === 0")
          template(#label)
            div.text-xs.font-medium.neutral700--text Hide empty columns
      v-spacer
      y-button(label="View full vital chart" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" unset-min-width size="xxs" height="24" button-class="px-2" @click="showVitalSignsDialog = true")

  .dialogs
    vital-signs-dialog(v-if="showVitalSignsDialog" :open.sync="showVitalSignsDialog" @updated="fetchVitalSign")

</template>

<script>
import { mapGetters } from 'vuex'
import VitalSignsDialog from '@/components/clinic/consultation/VitalSignsDialog.vue'

export default {
  components: { VitalSignsDialog },
  props: {
    type: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      isFetching: false,
      showVitalSignsDialog: false,
      vitalsigns: [],
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      pid: 'treatment/getLeadPid',
      storedHideEmptyColumns: 'clinic/getVitalSignHideEmptyColumns',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    HideEmptyColumns: {
      get () {
        return this.storedHideEmptyColumns
      },
      set (value) {
        this.$store.commit('clinic/setVitalSignHideEmptyColumns', value)
      }
    },
    hasHeight() {
      return this.vitalsigns.some(v => v.height)
    },
    hasWeight() {
      return this.vitalsigns.some(v => v.weight)
    },
    hasTemperature() {
      return this.vitalsigns.some(v => v.temperature)
    },
    hasBloodPressure() {
      return this.vitalsigns.some(v => v.bp_systolic || v.bp_diastolic)
    },
    hasPulse() {
      return this.vitalsigns.some(v => v.pulse)
    },
    hasSpo2() {
      return this.vitalsigns.some(v => v.spo2)
    },
    hasBloodGlucose() {
      return this.vitalsigns.some(v => v.blood_glucose)
    },
    hasRespiratoryRate() {
      return this.vitalsigns.some(v => v.respiratory_rate)
    },
    visibleColumnsCount() {
      if (!this.HideEmptyColumns) return 8
      return [
        this.hasHeight,
        this.hasWeight,
        this.hasTemperature,
        this.hasBloodPressure,
        this.hasPulse,
        this.hasSpo2,
        this.hasBloodGlucose,
        this.hasRespiratoryRate
      ].filter(Boolean).length
    }
  },
  mounted () {
    this.fetchVitalSign()
  },
  methods: {
    fetchVitalSign () {
      this.isFetching = true
      let params = {
        page_size: -1,
        ordering: 'recorded_at'
      }
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/consultations/${this.pid}/vital_signs`, { params })
        .then(resp => {
          if (resp.data?.results) {
            this.vitalsigns = resp.data.results
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    showColumn(column) {
      if (!this.HideEmptyColumns) return true
      
      switch(column) {
        case 'height': return this.hasHeight
        case 'weight': return this.hasWeight
        case 'temperature': return this.hasTemperature
        case 'bloodPressure': return this.hasBloodPressure
        case 'pulse': return this.hasPulse
        case 'spo2': return this.hasSpo2
        case 'bloodGlucose': return this.hasBloodGlucose
        case 'respiratoryRate': return this.hasRespiratoryRate
        default: return true
      }
    },
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .table-custom {
    .text-table-header, .v-data-table__wrapper > table > thead > tr > th {
      background-color: var(--v-neutral200);
      &:first-child {
        border-top-left-radius: 10px;
      }
      &:last-child {
        border-top-right-radius: 10px;
      }
    }
  }
}
</style>